import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { Input } from './ui/input';
import { Element as ElementType } from '../types';
import { Element as ElementComponent } from './index';
import { ScrollArea } from './ui/scroll-area';
import { Search } from 'lucide-react';
import ElementSizeSlider from './ElementSizeSlider';
import ElementSortingControl, { SortingOption } from './ElementSortingControl';
import { useDrop } from 'react-dnd';
import { useInteractionMode } from '../contexts/InteractionModeContext';
import { useReportingMode } from '../contexts/ReportingModeContext';
import { useGameState } from '@/contexts/GameStateContext';
import debounce from 'lodash/debounce';
import { Resizable } from 're-resizable';
import chroma from 'chroma-js';

interface ElementsPanelProps {
  elements: ElementType[];
  highlightCounts: Record<string, number>;
  onAddToFavorites?: (element: ElementType) => void; // Made optional since we're removing favorites
  onElementDragEnd?: (element: ElementType) => void;
  title: string;
  description?: string;
  favorites?: ElementType[]; // Keeping for compatibility
}

const ElementsPanel: React.FC<ElementsPanelProps> = ({
  elements,
  highlightCounts,
  onAddToFavorites,
  onElementDragEnd,
  title,
  description,
  favorites = [],
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortingOption, setSortingOption] = useState<SortingOption>('none');
  const [selectedForgeTab, setSelectedForgeTab] = useState<'Item' | 'Location' | 'Character'>(
    'Item'
  );

  // Get game state context for zoom level
  const { zoomLevel, setZoomLevel, isLoading } = useGameState();

  // Local state for element size with initial value from game state
  const [elementSize, setElementSize] = useState(zoomLevel || 2);

  // State for panel size - try to load from localStorage or use default
  const [size, setSize] = useState(() => {
    try {
      const savedHeight = localStorage.getItem('elementsPanel_height');
      return {
        height: savedHeight ? parseInt(savedHeight, 10) : 300,
        width: '100%',
      };
    } catch (error) {
      return { height: 300, width: '100%' };
    }
  });

  // Get interaction mode from context
  const { mode, selectedElement, setSelectedElement } = useInteractionMode();

  // Get reporting mode from context
  const { isReportingMode } = useReportingMode();

  // State to track if we're in delete element mode
  const [isDeleteElementMode, setIsDeleteElementMode] = useState(false);

  // Check for delete element mode
  useEffect(() => {
    const checkDeleteMode = () => {
      setIsDeleteElementMode(document.body.classList.contains('delete-element-mode'));
    };

    // Initial check
    checkDeleteMode();

    // Set up a mutation observer to watch for class changes on body
    const observer = new MutationObserver(checkDeleteMode);
    observer.observe(document.body, { attributes: true, attributeFilter: ['class'] });

    return () => observer.disconnect();
  }, []);

  // Update local state when zoom level changes in game state (only on initial load)
  useEffect(() => {
    if (!isLoading && zoomLevel) {
      setElementSize(zoomLevel);
    }
  }, [isLoading, zoomLevel]);

  // Debounced update of zoom level in game state when element size changes
  const debouncedSetZoomLevel = useCallback(
    debounce((newSize: number) => {
      if (!isLoading) {
        setZoomLevel(newSize);
      }
    }, 500),
    [setZoomLevel, isLoading]
  );

  // Update zoom level in game state when element size changes
  useEffect(() => {
    debouncedSetZoomLevel(elementSize);
    return () => debouncedSetZoomLevel.cancel();
  }, [elementSize, debouncedSetZoomLevel]);

  // Simplified tap destination handler - favorites functionality removed
  const handleTapDestination = () => {
    if (mode === 'tap-to-location' && selectedElement) {
      setSelectedElement(null);
    }
  };

  // Simplified drop ref - favorites functionality removed
  const [, dropRef] = useDrop({
    accept: 'element',
    drop: () => {
      return { handled: true, name: 'ElementsPanel' };
    },
  });

  const filteredElements = useMemo(() => {
    // First filter by search query
    let filtered = elements;
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = elements.filter((element) => element.name.toLowerCase().includes(query));
    }

    // Then apply sorting
    switch (sortingOption) {
      case 'alphabetical':
        return [...filtered].sort((a, b) => a.name.localeCompare(b.name));

      case 'forge':
        // Filter elements based on the selected forge tab
        // - Basic elements (Water, Fire, Dirt, Air) are shown in all tabs
        // - Discoveries are filtered by their forgeType property
        // - For backward compatibility, elements without forgeType are shown in the Item tab

        const basicElements = ['water', 'fire', 'dirt', 'air'];

        // First filter by forge type
        const forgeFiltered = [...filtered].filter((element) => {
          // Always include basic elements
          if (basicElements.includes(element.type.toLowerCase())) {
            return true;
          }

          // For discoveries, use the forgeType property if available
          if (element.forgeType) {
            return element.forgeType === selectedForgeTab;
          }

          // For older elements without forgeType (backward compatibility)
          // Default to showing in Item tab
          return selectedForgeTab === 'Item';
        });

        // Then sort: basic elements first, then alphabetically
        return forgeFiltered.sort((a, b) => {
          // Always put basic elements first in their defined order
          if (
            basicElements.includes(a.type.toLowerCase()) &&
            basicElements.includes(b.type.toLowerCase())
          ) {
            return (
              basicElements.indexOf(a.type.toLowerCase()) -
              basicElements.indexOf(b.type.toLowerCase())
            );
          }
          // Basic elements come before discoveries
          if (basicElements.includes(a.type.toLowerCase())) return -1;
          if (basicElements.includes(b.type.toLowerCase())) return 1;

          // Sort discoveries alphabetically
          return a.name.localeCompare(b.name);
        });

      case 'color':
        // Sort by color using HSL values for a visually pleasing gradient
        return [...filtered].sort((a, b) => {
          // Define a function to get the color safely
          const getColor = (element: ElementType): string => {
            if (element.color) return element.color;

            // Fallback colors for basic elements
            switch (element.type) {
              case 'water':
                return '#3b82f6';
              case 'fire':
                return '#ef4444';
              case 'dirt':
                return '#8B6F4E';
              case 'air':
                return '#e2e8f0';
              default:
                return '#8b5cf6';
            }
          };

          try {
            // Get colors for both elements
            const colorA = getColor(a);
            const colorB = getColor(b);

            // Convert to chroma color objects
            const chromaA = chroma(colorA);
            const chromaB = chroma(colorB);

            // Get HSL values for better perceptual sorting
            const hslA = chromaA.hsl();
            const hslB = chromaB.hsl();

            // If either color couldn't be parsed properly, fall back to string comparison
            if (!hslA || !hslB || hslA.some(isNaN) || hslB.some(isNaN)) {
              return colorA.localeCompare(colorB);
            }

            // Extract hue, saturation, lightness
            let [hueA, satA, lightA] = hslA;
            let [hueB, satB, lightB] = hslB;

            // Handle NaN values in hue (happens with grayscale colors)
            if (isNaN(hueA)) hueA = 0;
            if (isNaN(hueB)) hueB = 0;

            // Determine if colors are very dark (almost black)
            const isDarkA = lightA < 0.15;
            const isDarkB = lightB < 0.15;

            // Group very dark colors together at the beginning
            if (isDarkA && !isDarkB) return -1;
            if (!isDarkA && isDarkB) return 1;
            if (isDarkA && isDarkB) {
              // For two dark colors, sort by saturation then hue
              if (Math.abs(satA - satB) > 0.1) {
                return satB - satA; // More saturated dark colors first
              }
              return hueA - hueB;
            }

            // Determine if colors are grayscale (very low saturation)
            const isGrayA = satA < 0.12; // Increased threshold
            const isGrayB = satB < 0.12;

            // Handle grayscale colors - sort by lightness
            if (isGrayA && isGrayB) {
              return lightA - lightB; // Dark to light
            }

            // Put grayscale colors after dark colors but before colored ones
            if (isGrayA && !isGrayB) return -1;
            if (!isGrayA && isGrayB) return 1;

            // Handle hue wrapping for reds/pinks (hues near 0° and 360°)
            // This ensures reds at the beginning and end of the spectrum are grouped together
            if ((hueA > 330 && hueB < 30) || (hueB > 330 && hueA < 30)) {
              // Convert both to the same side of the spectrum for comparison
              const adjustedHueA = hueA > 330 ? hueA - 360 : hueA;
              const adjustedHueB = hueB > 330 ? hueB - 360 : hueB;
              return adjustedHueA - adjustedHueB;
            }

            // For colored elements, sort primarily by hue (color wheel order)
            // Increased threshold to keep similar colors together
            if (Math.abs(hueA - hueB) > 25) {
              return hueA - hueB;
            }

            // For similar hues, sort by saturation
            if (Math.abs(satA - satB) > 0.15) {
              return satB - satA; // More saturated first
            }

            // For similar saturation, sort by lightness
            return lightA - lightB; // Dark to light
          } catch (error) {
            // Fallback to string comparison if any error occurs
            console.error('Error sorting colors:', error);
            return getColor(a).localeCompare(getColor(b));
          }
        });

      case 'none':
      default:
        // Default sorting: basic elements first, then in the order they were discovered
        // Since we don't have a timestamp for when elements were discovered,
        // we'll use the array order as a proxy for discovery order
        return [...filtered].sort((a, b) => {
          // Always put Water, Fire, Dirt, Air first in that order
          if (
            ['Water', 'Fire', 'Dirt', 'Air'].includes(a.name) &&
            ['Water', 'Fire', 'Dirt', 'Air'].includes(b.name)
          ) {
            return (
              ['Water', 'Fire', 'Dirt', 'Air'].indexOf(a.name) -
              ['Water', 'Fire', 'Dirt', 'Air'].indexOf(b.name)
            );
          }
          // Basic elements come before discoveries
          if (['Water', 'Fire', 'Dirt', 'Air'].includes(a.name)) return -1;
          if (['Water', 'Fire', 'Dirt', 'Air'].includes(b.name)) return 1;

          // For discoveries, maintain the original order (which should be discovery order)
          // We can use the index in the original array as a proxy for discovery time
          const indexA = elements.findIndex((e) => e.id === a.id);
          const indexB = elements.findIndex((e) => e.id === b.id);
          return indexA - indexB;
        });
    }
  }, [elements, searchQuery, sortingOption, selectedForgeTab]);

  // We've moved the color logic directly into the sorting function

  // Calculate element size based on slider value
  const scaleFactor = elementSize; // Scale factor from 1 to 2
  const elementHeight = 28 * scaleFactor; // Base height * scale factor
  const elementWidth = 80 * scaleFactor; // Base width * scale factor

  // Handle resize stop to save the new height
  const handleResizeStop = (
    e: MouseEvent | TouchEvent,
    direction: string,
    ref: HTMLElement,
    d: { height: number }
  ) => {
    // Get the actual height from the element
    const newHeight = parseInt(ref.style.height, 10) || size.height;

    // Update the size state
    setSize({
      width: '100%',
      height: newHeight,
    });

    // Save to localStorage for persistence
    try {
      localStorage.setItem('elementsPanel_height', newHeight.toString());
    } catch (error) {
      console.error('Failed to save panel height to localStorage', error);
    }
  };

  return (
    <div
      ref={dropRef}
      onClick={handleTapDestination}
      className={`elements-panel bg-card rounded-xl shadow-lg p-4 border ${
        isReportingMode || isDeleteElementMode
          ? 'border-destructive ring-4 ring-destructive/20 relative z-50 shadow-xl !blur-none !pointer-events-auto'
          : 'border-border'
      } ${
        mode === 'tap-to-location' && selectedElement ? 'cursor-pointer hover:bg-accent/10' : ''
      }`}
    >
      <div className="flex justify-between items-center mb-1">
        <h3 className="text-lg font-bold">
          {title} ({elements.length})
        </h3>
        {isReportingMode && (
          <span className="bg-destructive text-destructive-foreground text-xs px-2 py-1 rounded-full font-medium animate-pulse">
            Reporting Mode
          </span>
        )}
        {isDeleteElementMode && (
          <span className="bg-destructive text-destructive-foreground text-xs px-2 py-1 rounded-full font-medium animate-pulse">
            Delete Mode
          </span>
        )}
      </div>
      {description && <p className="text-sm text-muted-foreground mb-4">{description}</p>}

      <div className="relative mb-2">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search elements..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-8"
        />
      </div>

      <div className="flex flex-col gap-2">
        <ElementSortingControl currentSorting={sortingOption} onSortingChange={setSortingOption} />

        {/* Forge Type Tabs - only show when sorting by forge */}
        {sortingOption === 'forge' && (
          <div className="flex border rounded-md overflow-hidden mb-2">
            <button
              className={`flex-1 py-1 text-xs font-medium ${
                selectedForgeTab === 'Item'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card hover:bg-accent/50'
              }`}
              onClick={() => setSelectedForgeTab('Item')}
            >
              Item
            </button>
            <button
              className={`flex-1 py-1 text-xs font-medium ${
                selectedForgeTab === 'Location'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card hover:bg-accent/50'
              }`}
              onClick={() => setSelectedForgeTab('Location')}
            >
              Location
            </button>
            <button
              className={`flex-1 py-1 text-xs font-medium ${
                selectedForgeTab === 'Character'
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-card hover:bg-accent/50'
              }`}
              onClick={() => setSelectedForgeTab('Character')}
            >
              Character
            </button>
          </div>
        )}

        <ElementSizeSlider value={elementSize} onChange={setElementSize} />
      </div>

      <Resizable
        size={size}
        onResizeStop={handleResizeStop}
        onResize={(e, direction, ref) => {
          // Update size in real-time during resize
          setSize({
            width: '100%',
            height: parseInt(ref.style.height, 10) || size.height,
          });
        }}
        enable={{ bottom: true }}
        minHeight={150}
        maxHeight={1200}
        handleComponent={{
          bottom: (
            <div className="h-4 w-full cursor-ns-resize flex items-center justify-center">
              <div className="flex flex-col items-center justify-center gap-1">
                <div className="w-8 h-1 bg-muted-foreground/60 rounded-full" />
                <div className="w-8 h-1 bg-muted-foreground/60 rounded-full" />
              </div>
            </div>
          ),
        }}
        handleStyles={{
          bottom: {
            bottom: 0,
            height: '8px',
            cursor: 'ns-resize',
            width: '100%',
            zIndex: 10,
          },
        }}
        handleClasses={{
          bottom: 'resize-handle-bottom',
        }}
      >
        <ScrollArea className="pr-4 overflow-hidden" style={{ height: `${size.height - 16}px` }}>
          {filteredElements.length > 0 ? (
            <div className="flex flex-row flex-wrap gap-1 pr-2 pt-2 pb-6 px-1">
              {filteredElements.map((element) => (
                <ElementComponent
                  key={element.id}
                  element={element}
                  className="h-auto"
                  isHighlighted={highlightCounts[element.id] > 0}
                  onDragEnd={() => {
                    if (onElementDragEnd) onElementDragEnd(element);
                  }}
                  context="panel"
                  style={{
                    height: `${elementHeight}px`,
                    minWidth: `${elementWidth}px`,
                    fontSize: `${14 * Math.sqrt(elementSize)}px`,
                    padding: `${4 * elementSize}px ${6 * elementSize}px`,
                    margin: '1px',
                    display: 'inline-flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="flex items-center justify-center h-40">
              <p className="text-muted-foreground">No elements found</p>
            </div>
          )}
        </ScrollArea>
      </Resizable>
    </div>
  );
};

export default ElementsPanel;
